import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import {
  MapPin, Users, Bell, Store, Wrench, CalendarDays, Share2,
  Heart, MessageCircle, Sparkles, Clock, ArrowRight, MessageSquare,
} from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/36202135/pexels-photo-36202135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1400";

function relTime(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } }
function fmtEvent(iso) { try { return format(new Date(iso), "EEE, MMM d · h:mm a"); } catch { return iso; } }

export default function Community() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${name} · Community · Jirani`;
    (async () => {
      try {
        const { data } = await api.get(`/communities/${encodeURIComponent(name)}`);
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [name]);

  const inviteText = () => {
    const url = window.location.href;
    return data
      ? `Karibu ${data.name} on Jirani.co — ${data.totals.members} neighbours already onboard. Join us at ${url}`
      : `Join us on Jirani.co: ${url}`;
  };

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(inviteText())}`, "_blank", "noopener");
  };

  const shareViaSMS = () => {
    // Opens the device's SMS composer with a pre-filled invite.
    // Using the ?body= form works on both iOS and Android.
    const body = encodeURIComponent(inviteText());
    // Note: empty recipient lets the user pick from contacts.
    window.location.href = `sms:?&body=${body}`;
  };

  if (loading) {
    return <div className="grid min-h-[60vh] place-items-center text-[#4A4A4A]">Loading…</div>;
  }
  if (!data) return null;

  const { totals, posts = [], events = [], alerts = [] } = data;

  return (
    <div data-testid="community-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="relative h-[280px] w-full sm:h-[340px]">
          <img src={HERO_IMG} alt={data.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-[#2E473B]" />
        </div>

        <div className="relative -mt-24 sm:-mt-32">
          <div className="mx-auto max-w-6xl px-4 pb-6 sm:px-6 lg:px-8">
            <div className="jirani-card rounded-3xl border border-black/5 bg-white p-6 shadow-[0_30px_60px_-30px_rgba(46,71,59,0.4)] sm:p-8">
              <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                <div>
                  <div className="jirani-eyebrow">Neighbourhood community</div>
                  <h1
                    className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A] sm:text-5xl"
                    style={{ fontFamily: "Outfit", fontWeight: 800 }}
                  >
                    <MapPin className="h-8 w-8 text-[#D95D39]" />
                    {data.name}
                  </h1>
                  <p className="mt-2 max-w-xl text-[#4A4A4A]">
                    Karibu the {data.name} corner of Jirani — everything happening in your neighbourhood in one warm place.
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    data-testid="community-share-btn"
                    onClick={shareToWhatsApp}
                    className="jirani-pill h-11 rounded-full bg-[#25D366] px-5 text-white hover:bg-[#20b757]"
                  >
                    <Share2 className="mr-2 h-4 w-4" /> Share on WhatsApp
                  </Button>
                  <Button
                    data-testid="community-sms-invite-btn"
                    onClick={shareViaSMS}
                    variant="outline"
                    className="jirani-pill h-11 rounded-full border-[#D95D39]/40 bg-white px-5 text-[#D95D39] hover:bg-[#D95D39]/5"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" /> Text invite
                  </Button>
                </div>
              </div>

              <dl
                data-testid="community-stats"
                className="mt-6 grid grid-cols-2 gap-4 border-t border-black/5 pt-6 sm:grid-cols-4"
              >
                {[
                  { key: "members", label: "Members",      icon: Users,        color: "text-[#2E473B]" },
                  { key: "posts",   label: "Posts",        icon: Sparkles,     color: "text-[#D95D39]" },
                  { key: "events",  label: "Events",       icon: CalendarDays, color: "text-[#F4A261]" },
                  { key: "alerts",  label: "Alerts",       icon: Bell,         color: "text-[#C1292E]" },
                ].map(({ key, label, icon: Icon, color }) => (
                  <div key={key}>
                    <dt className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#2E473B]/70">
                      <Icon className={`h-3.5 w-3.5 ${color}`} /> {label}
                    </dt>
                    <dd className="mt-1 text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                      {totals[key] || 0}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="mx-auto grid max-w-6xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-3 lg:px-8">
        {/* Recent posts */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="jirani-eyebrow">Habari · Latest posts</div>
              <h2 className="mt-1 text-2xl text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
                From your neighbours
              </h2>
            </div>
            <Link to="/feed" data-testid="community-see-feed" className="jirani-pill inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-[#2E473B] hover:bg-[#F9F7F4]">
              See feed <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]">
              No posts yet in {data.name}. Be the first to say habari!
            </div>
          ) : (
            <ul className="space-y-4">
              {posts.map((p) => (
                <li
                  key={p.id}
                  data-testid={`community-post-${p.id}`}
                  className="jirani-card rounded-2xl border border-black/5 bg-white p-5"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-[#F4A261]/30 text-sm font-semibold text-[#2E473B]">
                      {(p.author?.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#1A1A1A]">{p.author?.name || "A neighbor"}</div>
                      <div className="text-[11px] text-[#4A4A4A]">{relTime(p.created_at)}</div>
                    </div>
                    <Badge className="ml-auto rounded-full bg-[#F9F7F4] text-[#2E473B] hover:bg-[#F9F7F4]">
                      {p.category}
                    </Badge>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-[#1A1A1A]">{p.content}</p>
                  <div className="mt-3 flex gap-4 text-xs text-[#4A4A4A]">
                    <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {(p.likes || []).length}</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {p.comment_count || 0}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right column */}
        <aside className="space-y-6">
          {/* Upcoming events */}
          <div className="jirani-card rounded-2xl border border-black/5 bg-white p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
              <CalendarDays className="h-4 w-4 text-[#D95D39]" /> Upcoming events
            </div>
            {events.length === 0 ? (
              <div className="text-xs text-[#4A4A4A]">No events yet — plan one!</div>
            ) : (
              <ul className="space-y-3">
                {events.map((ev) => (
                  <li
                    key={ev.id}
                    data-testid={`community-event-${ev.id}`}
                    className="flex items-start gap-3 rounded-xl bg-[#F9F7F4] p-3"
                  >
                    <div className="grid shrink-0 rounded-lg bg-white p-2 text-center leading-none">
                      <span className="text-[10px] uppercase tracking-widest text-[#2E473B]/70">
                        {new Date(ev.starts_at).toLocaleString("en", { month: "short" })}
                      </span>
                      <span className="text-lg font-bold text-[#D95D39]" style={{ fontFamily: "Outfit" }}>
                        {new Date(ev.starts_at).getDate()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#1A1A1A]">{ev.title}</div>
                      <div className="text-[11px] text-[#2E473B]/80">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtEvent(ev.starts_at)}</span>
                      </div>
                      <div className="text-[11px] text-[#2E473B]/80">
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/events" data-testid="community-see-events" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#D95D39] hover:underline">
              See all events <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Recent alerts */}
          <div className="jirani-card rounded-2xl border border-black/5 bg-white p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
              <Bell className="h-4 w-4 text-[#C1292E]" /> Recent alerts
            </div>
            {alerts.length === 0 ? (
              <div className="text-xs text-[#4A4A4A]">All quiet — that's a good thing!</div>
            ) : (
              <ul className="space-y-2">
                {alerts.map((a) => (
                  <li
                    key={a.id}
                    data-testid={`community-alert-${a.id}`}
                    className="rounded-xl bg-[#F9F7F4] p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-[#1A1A1A]">{a.title}</div>
                      <Badge className={`rounded-full text-[10px] ${
                        a.severity === "high" ? "bg-red-100 text-red-800 hover:bg-red-100"
                        : a.severity === "medium" ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                        : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                      }`}>{a.severity}</Badge>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-[#4A4A4A]">{a.description}</p>
                    <div className="mt-1.5 text-[10px] text-[#2E473B]/70">{a.kind} · {relTime(a.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/alerts" data-testid="community-see-alerts" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#D95D39] hover:underline">
              See all alerts <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Directory + marketplace CTAs */}
          <div className="jirani-card rounded-2xl border border-black/5 bg-[#2E473B] p-5 text-white">
            <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>
              Explore {data.name}
            </div>
            <div className="mt-3 space-y-2">
              <Link to="/directory" className="flex items-center gap-2 rounded-xl bg-white/10 p-3 text-sm hover:bg-white/15">
                <Wrench className="h-4 w-4 text-[#F4A261]" /> Local businesses
              </Link>
              <Link to="/marketplace" className="flex items-center gap-2 rounded-xl bg-white/10 p-3 text-sm hover:bg-white/15">
                <Store className="h-4 w-4 text-[#F4A261]" /> Marketplace
              </Link>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
